import React, { useEffect, useRef, useState } from "react";

/**
 * Full-bleed hero plate for the home page.
 *
 * Replaces the abstract disc that stood here. The subject is the product:
 * a vehicle closing on the viewer down a lit path. The loop rides the approach,
 * so the car never arrives and the loop never shows a seam.
 *
 * Layering, back to front: video (or its still), then the scrim that buys the
 * headline its contrast, then the copy.
 *
 * The still is not a nicety. It is the poster while the video buffers, what a
 * reduced-motion visitor sees, and the whole plate on a narrow screen, where
 * autoplay is unreliable and a multi-megabyte download is rude.
 *
 * The still is frame zero of the video, not a separately generated plate, so the
 * poster and the first played frame are the same picture and nothing pops when
 * playback starts.
 *
 * Set HERO_MEDIA to null to fall back to a plain black field, which is a correct
 * hero, just a quiet one.
 */
const HERO_MEDIA = {
  still: "/assets/hero/ppm-hero-approach.jpg",
  video: [
    { src: "/assets/hero/ppm-hero-approach.webm", type: "video/webm" },
    { src: "/assets/hero/ppm-hero-approach.mp4", type: "video/mp4" },
  ],
};

function prefersReducedMotion() {
  return typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function isNarrow() {
  return typeof window !== "undefined"
    && window.matchMedia
    && window.matchMedia("(max-width: 860px)").matches;
}

export default function HeroVehicle() {
  const videoRef = useRef(null);
  const [motion, setMotion] = useState(false);

  useEffect(() => {
    const decide = () => setMotion(!prefersReducedMotion() && !isNarrow());
    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
  }, []);

  useEffect(() => {
    const node = videoRef.current;
    if (!node || !motion) return undefined;
    // Autoplay can be refused. A refused play is not an error worth logging,
    // and the poster is already the right thing to be looking at.
    const attempt = node.play();
    if (attempt && typeof attempt.catch === "function") attempt.catch(() => {});
    return undefined;
  }, [motion]);

  if (!HERO_MEDIA) return <div className="hero-plate hero-plate-empty" aria-hidden="true" />;

  return (
    <div className="hero-plate" aria-hidden="true">
      {motion ? (
        <video
          ref={videoRef}
          className="hero-plate-media"
          poster={HERO_MEDIA.still}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          tabIndex={-1}
        >
          {HERO_MEDIA.video.map((source) => (
            <source key={source.src} src={source.src} type={source.type} />
          ))}
        </video>
      ) : (
        <img
          className="hero-plate-media"
          src={HERO_MEDIA.still}
          alt=""
          width="1920"
          height="1080"
          decoding="sync"
          fetchPriority="high"
        />
      )}
      <div className="hero-plate-scrim" />
    </div>
  );
}
